import React, { useState, useEffect, useCallback } from 'react';
import {
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  Tooltip,
  Grid,
  Card,
  CardContent,
  FormControlLabel,
  Checkbox,
  Divider,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Refresh,
  Check,
  Security,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { usePermissions } from '../../context/PermissionContext';
import { AdminGuard } from '../../components/PermissionGuard';
import API_BASE_URL from '../../config/api';

const RoleManagement = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { isAdmin } = usePermissions();

  // State
  const [roles, setRoles] = useState([]);
  const [interfaces, setInterfaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState('create'); // 'create' or 'edit'
  const [selectedRole, setSelectedRole] = useState(null);
  const [roleForm, setRoleForm] = useState({
    roleName: '',
    description: '',
    isDefault: false,
    interfaceIds: [],
  });

  // Fetch roles
  const fetchRoles = useCallback(async () => {
    try {
      const token = localStorage.getItem('jwtToken');
      const response = await fetch(`${API_BASE_URL}/api/AccessControl/roles`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      const data = await response.json();
      if (data.success) {
        setRoles(data.roles || []);
      }
    } catch (err) {
      setError('Failed to fetch roles');
    }
  }, []);

  // Fetch interfaces
  const fetchInterfaces = useCallback(async () => {
    try {
      const token = localStorage.getItem('jwtToken');
      const response = await fetch(`${API_BASE_URL}/api/AccessControl/interfaces`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      const data = await response.json();
      if (data.success) {
        setInterfaces(data.interfaces || []);
      }
    } catch (err) {
      console.error('Failed to fetch interfaces', err);
    }
  }, []);

  // Fetch role details
  const fetchRoleDetails = useCallback(async (roleId) => {
    try {
      const token = localStorage.getItem('jwtToken');
      const response = await fetch(`${API_BASE_URL}/api/AccessControl/roles/${roleId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      const data = await response.json();
      if (data.success) {
        setRoleForm({
          roleName: data.role.roleName,
          description: data.role.description || '',
          isDefault: data.role.isDefault,
          interfaceIds: data.interfaces?.map(i => i.interfaceId) || [],
        });
      }
    } catch (err) {
      setError('Failed to fetch role details');
    }
  }, []);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchRoles(), fetchInterfaces()]);
      setLoading(false);
    };
    loadData();
  }, [fetchRoles, fetchInterfaces]);

  // Handlers
  const handleCreateRole = () => {
    setDialogMode('create');
    setSelectedRole(null);
    setRoleForm({
      roleName: '',
      description: '',
      isDefault: false,
      interfaceIds: [],
    });
    setDialogOpen(true);
  };

  const handleEditRole = async (role) => {
    setDialogMode('edit');
    setSelectedRole(role);
    await fetchRoleDetails(role.roleId);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedRole(null);
    setRoleForm({
      roleName: '',
      description: '',
      isDefault: false,
      interfaceIds: [],
    });
  };

  const handleSaveRole = async () => {
    try {
      const token = localStorage.getItem('jwtToken');
      const url = dialogMode === 'create'
        ? `${API_BASE_URL}/api/AccessControl/roles`
        : `${API_BASE_URL}/api/AccessControl/roles/${selectedRole.roleId}`;

      const response = await fetch(url, {
        method: dialogMode === 'create' ? 'POST' : 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(roleForm),
      });

      const data = await response.json();
      if (data.success) {
        setSuccess(dialogMode === 'create' ? 'Role created successfully' : 'Role updated successfully');
        handleCloseDialog();
        fetchRoles();
      } else {
        setError(data.message || 'Failed to save role');
      }
    } catch (err) {
      setError('Failed to save role');
    }
  };

  const handleDeleteRole = async (roleId) => {
    if (!window.confirm('Are you sure you want to delete this role?')) return;

    try {
      const token = localStorage.getItem('jwtToken');
      const response = await fetch(`${API_BASE_URL}/api/AccessControl/roles/${roleId}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();
      if (data.success) {
        setSuccess('Role deleted successfully');
        fetchRoles();
      } else {
        setError(data.message || 'Failed to delete role');
      }
    } catch (err) {
      setError('Failed to delete role');
    }
  };

  const handleInterfaceToggle = (interfaceId) => {
    setRoleForm(prev => ({
      ...prev,
      interfaceIds: prev.interfaceIds.includes(interfaceId)
        ? prev.interfaceIds.filter(id => id !== interfaceId)
        : [...prev.interfaceIds, interfaceId],
    }));
  };

  const categoryDisplayNames = {
    pdf: 'PDF', excel: 'Excel', links: 'Links', other: 'Other', ai: 'AI', analytics: 'Analytics'
  };

  // Interfaces a category "select all" must skip (the Reorder Console API honours a role grant only when it was ticked
  // on purpose; see ReorderConsoleAccess.cs).
  const OWN_TICK_ONLY_KEYS = ['reorder-console', 'reorder-console-costs'];
  const isOwnTickOnly = (iface) => OWN_TICK_ONLY_KEYS.includes(((iface && iface.interfaceKey) || '').toLowerCase());

  const handleSelectAllCategory = (categoryKey) => {
    // Tools that show sensitive data are never swept in by a category "select all": they need their own tick.
    const categoryInterfaceIds = interfaces
      .filter(i => (i.category || '').toLowerCase().trim() === categoryKey && !isOwnTickOnly(i))
      .map(i => i.interfaceId);

    const allSelected = categoryInterfaceIds.every(id => roleForm.interfaceIds.includes(id));

    if (allSelected) {
      setRoleForm(prev => ({
        ...prev,
        interfaceIds: prev.interfaceIds.filter(id => !categoryInterfaceIds.includes(id)),
      }));
    } else {
      setRoleForm(prev => ({
        ...prev,
        interfaceIds: [...new Set([...prev.interfaceIds, ...categoryInterfaceIds])],
      }));
    }
  };

  // Group interfaces by category (normalize to lowercase to avoid duplicates)
  const interfacesByCategory = interfaces.reduce((acc, iface) => {
    const key = (iface.category || 'other').toLowerCase().trim();
    if (!acc[key]) {
      acc[key] = { displayName: categoryDisplayNames[key] || key.charAt(0).toUpperCase() + key.slice(1), items: [] };
    }
    acc[key].items.push(iface);
    return acc;
  }, {});

  // Clear messages after 5 seconds
  useEffect(() => {
    if (success || error) {
      const timer = setTimeout(() => {
        setSuccess('');
        setError('');
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [success, error]);

  return (
    <AdminGuard>
      <Box sx={{ p: { xs: 1.5, md: 3 } }}>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, fontSize: { xs: '1.5rem', md: '2.125rem' } }}>
          Role Management
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Create and manage roles with specific interface permissions
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

        {/* Actions */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, gap: 2, flexWrap: 'wrap' }}>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleCreateRole}
          >
            Create Role
          </Button>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={fetchRoles}
          >
            Refresh
          </Button>
        </Box>

        {/* Roles Table */}
        <TableContainer component={Paper} elevation={2} sx={{ overflowX: 'auto' }}>
          <Table sx={{ minWidth: 640 }}>
            <TableHead>
              <TableRow sx={{
                backgroundColor: theme.palette.mode === 'dark'
                  ? theme.palette.grey[800]
                  : theme.palette.grey[100]
              }}>
                <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>Role Name</TableCell>
                <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>Description</TableCell>
                <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>Type</TableCell>
                <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>Status</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : roles.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                    No roles found
                  </TableCell>
                </TableRow>
              ) : (
                roles.map((role) => (
                  <TableRow key={role.roleId} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Security color="primary" />
                        <Typography fontWeight={500}>
                          {role.roleName}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>{role.description || '-'}</TableCell>
                    <TableCell>
                      {role.isDefault && (
                        <Chip
                          label="Default"
                          color="primary"
                          size="small"
                          variant="outlined"
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={role.isActive ? 'Active' : 'Inactive'}
                        color={role.isActive ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Edit Role">
                        <IconButton onClick={() => handleEditRole(role)} size="small">
                          <Edit />
                        </IconButton>
                      </Tooltip>
                      {!role.isDefault && (
                        <Tooltip title="Delete Role">
                          <IconButton
                            onClick={() => handleDeleteRole(role.roleId)}
                            size="small"
                            color="error"
                          >
                            <Delete />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Create/Edit Role Dialog */}
        <Dialog
          open={dialogOpen}
          onClose={handleCloseDialog}
          maxWidth="md"
          fullWidth
          fullScreen={isMobile}
        >
          <DialogTitle>
            {dialogMode === 'create' ? 'Create New Role' : `Edit Role: ${selectedRole?.roleName}`}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 1 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Role Name"
                    fullWidth
                    required
                    value={roleForm.roleName}
                    onChange={(e) => setRoleForm(prev => ({ ...prev, roleName: e.target.value }))}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Description"
                    fullWidth
                    value={roleForm.description}
                    onChange={(e) => setRoleForm(prev => ({ ...prev, description: e.target.value }))}
                  />
                </Grid>
              </Grid>

              <Divider sx={{ my: 3 }} />

              <Typography variant="subtitle1" gutterBottom fontWeight={500}>
                Interface Permissions
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Select which interfaces users with this role can access
              </Typography>

              {Object.entries(interfacesByCategory).map(([categoryKey, group]) => {
                const categoryInterfaces = group.items;
                const categoryInterfaceIds = categoryInterfaces.filter(i => !isOwnTickOnly(i)).map(i => i.interfaceId);
                const allSelected = categoryInterfaceIds.length > 0 && categoryInterfaceIds.every(id => roleForm.interfaceIds.includes(id));
                const someSelected = categoryInterfaceIds.some(id => roleForm.interfaceIds.includes(id));

                return (
                  <Box key={categoryKey} sx={{ mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={allSelected}
                            indeterminate={someSelected && !allSelected}
                            onChange={() => handleSelectAllCategory(categoryKey)}
                          />
                        }
                        label={
                          <Typography variant="subtitle2">
                            {group.displayName} Tools ({categoryInterfaces.length})
                          </Typography>
                        }
                      />
                    </Box>
                    <Grid container spacing={1} sx={{ pl: { xs: 1, sm: 4 } }}>
                      {categoryInterfaces.map((iface) => (
                        <Grid item xs={12} sm={6} md={4} key={iface.interfaceId}>
                          <Card
                            variant="outlined"
                            sx={{
                              cursor: 'pointer',
                              backgroundColor: roleForm.interfaceIds.includes(iface.interfaceId)
                                ? theme.palette.mode === 'dark'
                                  ? 'rgba(144, 202, 249, 0.16)'
                                  : 'rgba(25, 118, 210, 0.08)'
                                : 'transparent',
                              borderColor: roleForm.interfaceIds.includes(iface.interfaceId)
                                ? theme.palette.primary.main
                                : theme.palette.divider,
                              '&:hover': {
                                backgroundColor: theme.palette.mode === 'dark'
                                  ? 'rgba(255, 255, 255, 0.08)'
                                  : 'rgba(0, 0, 0, 0.04)',
                              },
                            }}
                            onClick={() => handleInterfaceToggle(iface.interfaceId)}
                          >
                            <CardContent sx={{ py: 1, px: 1.5, '&:last-child': { pb: 1 } }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Typography variant="body2" noWrap sx={{ flex: 1 }}>
                                  {iface.interfaceName}
                                </Typography>
                                {roleForm.interfaceIds.includes(iface.interfaceId) && (
                                  <Check color="primary" fontSize="small" />
                                )}
                              </Box>
                              {isOwnTickOnly(iface) && (
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.3 }}>
                                  Vendor and cost data: tick it on its own (not included in select all)
                                </Typography>
                              )}
                            </CardContent>
                          </Card>
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                );
              })}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleSaveRole}
              disabled={!roleForm.roleName.trim()}
            >
              {dialogMode === 'create' ? 'Create Role' : 'Save Changes'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </AdminGuard>
  );
};

export default RoleManagement;
